import { useEffect, useState, type ReactElement } from 'react';
import { Link } from 'react-router';
import type { OptInMethod, Uuid } from '@zenda/api-contract';
import { Badge } from './Badge';
import { Drawer } from './Drawer';
import { Icon } from './Icon';
import { Skeleton } from './Skeleton';
import { CampoData } from './CampoData';
import { Dependentes } from './Dependentes';
import { FichaEstendida } from './FichaEstendida';
import { METODOS_DE_OPT_IN } from './NovoPaciente';
import { useIrParaConversa } from './IniciarConversa';
import { usePatient, usePatientMutations } from '../lib/queries';
import {
  dataDeHoje,
  formatDateFull,
  formatRelative,
  formatPhone,
  toDateInput,
} from '../lib/format';
import { useT } from '../lib/i18n';
import { appointmentStatus } from '../lib/labels';
import { rotaDaConversa } from '../lib/rotas';

/** Quanto tempo o campo espera parado antes de salvar sozinho. */
const AUTOSAVE_MS = 900;

export function PatientDrawer({
  clientId,
  contactId,
  aviso = null,
  onClose,
}: {
  readonly clientId: Uuid;
  readonly contactId: Uuid;
  /** Mostrado no topo: o cadastro caiu num paciente que já existia. */
  readonly aviso?: string | null;
  readonly onClose: () => void;
}): ReactElement {
  const t = useT();
  const detail = usePatient(clientId, contactId);
  const { save } = usePatientMutations(clientId, contactId);

  const [notes, setNotes] = useState('');
  const [notesTouched, setNotesTouched] = useState(false);
  const [salvou, setSalvou] = useState(false);
  const [metodo, setMetodo] = useState<OptInMethod>('presencial');
  const { ir, indo } = useIrParaConversa();

  const dados = detail.data;
  const p = dados?.patient;

  // Sincroniza a anotacao vinda do servidor apenas enquanto ela NAO foi tocada. Sem
  // essa guarda, um refetch no meio da digitacao sobrescreveria o que ela escreveu.
  useEffect(() => {
    if (!notesTouched && detail.data) setNotes(detail.data.notes ?? '');
  }, [detail.data, notesTouched]);

  // Salva sozinho depois de a pessoa parar de digitar. Botao "salvar" numa anotacao
  // solta e o tipo de coisa que se esquece de clicar — e a anotacao se perde.
  useEffect(() => {
    if (!notesTouched) return;
    const t = window.setTimeout(() => {
      save.mutate(
        { notes: notes.trim() === '' ? null : notes },
        {
          onSuccess: () => {
            setSalvou(true);
            window.setTimeout(() => setSalvou(false), 2000);
          },
        },
      );
      setNotesTouched(false);
    }, AUTOSAVE_MS);
    return () => window.clearTimeout(t);
  }, [notes, notesTouched, save]);

  return (
    <Drawer
      title={p?.name ?? t.patients.patientFallback}
      subtitle={
        p === undefined ? null : (
          <>
            <span className="num">{formatPhone(p.phone ?? p.waId)}</span>
            <span className="dot" style={{ background: p.clientColor }} />
            <span>{p.clientName}</span>
          </>
        )
      }
      onClose={onClose}
      footer={
        <>
          <span className="dsec__hint">
            {salvou ? <span className="dsaved">{t.patients.saved}</span> : t.patients.autoSaved}
          </span>
          {p === undefined ? null : p.conversationId === null ? (
            <button
              type="button"
              className="btn btn--primary btn--sm"
              disabled={indo}
              onClick={() => ir(clientId, contactId)}
            >
              <Icon name="chat" size={15} />
              {t.patients.sendMessage}
            </button>
          ) : (
            <Link to={rotaDaConversa(p.conversationId)} className="btn btn--primary btn--sm">
              <Icon name="chat" size={15} />
              {t.patients.openConversation}
            </Link>
          )}
        </>
      }
    >
      {detail.isPending || dados === undefined || p === undefined ? (
        <>
          <Skeleton h={84} radius="var(--radius-md)" />
          <Skeleton h={140} radius="var(--radius-md)" />
          <Skeleton h={120} radius="var(--radius-md)" />
        </>
      ) : (
        <>
          {aviso === null ? null : <p className="dsec__hint">{aviso}</p>}
          <dl className="dfacts">
            <div>
              <dt>{t.patients.conversations}</dt>
              <dd>{p.conversationCount}</dd>
            </div>
            <div>
              <dt>{t.patients.appointments}</dt>
              <dd>{p.appointmentCount}</dd>
            </div>
            <div>
              <dt>{t.patients.patientSince}</dt>
              <dd style={{ fontSize: 13 }}>
                {p.firstSeenAt === null ? '—' : formatDateFull(p.firstSeenAt)}
              </dd>
            </div>
          </dl>

          {/* ── Ficha ─────────────────────────────────────────────────────── */}
          <section className="dsec">
            <div className="dsec__head">
              <span className="dsec__title">{t.patients.notesTitle}</span>
            </div>
            <textarea
              className="dnote"
              value={notes}
              maxLength={4000}
              onChange={(e) => {
                setNotes(e.target.value);
                setNotesTouched(true);
              }}
              placeholder={t.patients.notesPlaceholder}
            />
            <p className="dsec__hint">{t.patients.notesHint}</p>

            <div className="field">
              <label className="label" htmlFor="pd-birth">
                {t.patients.birthDate}
              </label>
              <CampoData
                id="pd-birth"
                value={toDateInput(p.birthDate)}
                onChange={(d) => save.mutate({ birthDate: d === '' ? null : d })}
                max={dataDeHoje()}
              />
            </div>
          </section>

          <FichaEstendida
            nome={p.name}
            telefone={formatPhone(p.phone ?? p.waId)}
            telefoneTravado={p.phoneLocked}
            ficha={dados.record}
            salvar={(mudanca) => save.mutate(mudanca)}
            erro={save.isError ? save.error.message : null}
          />

          <section className="dsec">
            <div className="dsec__head">
              <span className="dsec__title">{t.patients.optIn.title}</span>
            </div>
            {p.optInAt === null ? (
              <>
                <p className="dsec__hint">{t.patients.optIn.none}</p>
                <div className="form__row">
                  <select
                    className="select select--sm"
                    aria-label={t.patients.optIn.method}
                    value={metodo}
                    onChange={(e) => setMetodo(e.target.value as OptInMethod)}
                  >
                    {METODOS_DE_OPT_IN.map((m) => (
                      <option key={m} value={m}>
                        {t.patients.optIn.methods[m]}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    disabled={save.isPending}
                    onClick={() => save.mutate({ optInMethod: metodo })}
                  >
                    {t.patients.optIn.register}
                  </button>
                </div>
              </>
            ) : (
              <div className="form__row">
                <span>{t.patients.optIn.given(formatDateFull(p.optInAt))}</span>
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  disabled={save.isPending}
                  onClick={() => save.mutate({ optInMethod: null })}
                >
                  {t.patients.optIn.remove}
                </button>
              </div>
            )}
          </section>

          <Dependentes clientId={clientId} contactId={contactId} dependentes={dados.dependents} />

          {/* ── Histórico ─────────────────────────────────────────────────── */}
          <section className="dsec">
            <div className="dsec__head">
              <span className="dsec__title">{t.patients.appointments}</span>
            </div>
            {dados.appointments.length === 0 ? (
              <p className="dlist__empty">{t.patients.appointmentsNone}</p>
            ) : (
              <div className="dlist">
                {dados.appointments.slice(0, 6).map((a) => (
                  <div className="dlist__item" key={a.id}>
                    <span className="dlist__mark" style={{ background: a.doctorColor }} />
                    <div className="dlist__main">
                      <span className="dlist__name">{a.title ?? t.patients.consultation}</span>
                      <span className="dlist__meta">
                        {formatDateFull(a.startsAt)} · {a.doctorName}
                        {a.dependentName === null ? '' : ` · ${a.dependentName}`}
                      </span>
                    </div>
                    <Badge tone={appointmentStatus(t, a.status).tone}>
                      {appointmentStatus(t, a.status).text}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="dsec">
            <div className="dsec__head">
              <span className="dsec__title">{t.patients.conversations}</span>
            </div>
            {dados.conversations.length === 0 ? (
              <p className="dlist__empty">{t.patients.conversationsNone}</p>
            ) : null}
            <div className="dlist">
              {dados.conversations.map((c) => (
                <Link className="dlist__item" key={c.id} to={rotaDaConversa(c.id)}>
                  <div className="dlist__main">
                    <span className="dlist__name">
                      {c.lastMessagePreview ?? t.inbox.noMessages}
                    </span>
                    <span className="dlist__meta">
                      {c.doctorName}
                      {c.lastMessageAt === null ? '' : ` · ${formatRelative(c.lastMessageAt)}`}
                    </span>
                  </div>
                  <Icon name="chevron-right" size={15} />
                </Link>
              ))}
            </div>
          </section>
        </>
      )}
    </Drawer>
  );
}
